import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Save, Tag, ChevronDown, ChevronRight } from 'lucide-react';
import { documentTemplateService } from '../../services/api';
import useModal from '../../hooks/useModal';
import useNotification from '../../hooks/useNotification';
import Notification from '../common/Notification';

const DocumentTemplateEditor = ({ template, onClose, categories = [] }) => {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [category, setCategory] = useState(template?.category || '');
  const [practiceArea, setPracticeArea] = useState(template?.practice_area || '');
  const [content, setContent] = useState(template?.content || '');
  const [placeholders, setPlaceholders] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);
  const { notification, showError, hideNotification } = useNotification();
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, () => onClose(false));

  useEffect(() => {
    documentTemplateService.getPlaceholders().then(res => {
      setPlaceholders(res.data);
      // Expand all groups by default
      const groups = {};
      res.data.forEach(p => { groups[p.group] = true; });
      setExpandedGroups(groups);
    }).catch(() => {});
  }, []);

  const insertPlaceholder = useCallback((key) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const tag = `{{${key}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.substring(0, start) + tag + content.substring(end);
    setContent(newContent);
    // Restore cursor position after the inserted tag
    setTimeout(() => {
      textarea.focus();
      const newPos = start + tag.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  }, [content]);

  const handleSave = async () => {
    if (!name.trim()) {
      showError('Template name is required');
      return;
    }
    try {
      setSaving(true);
      // Extract used placeholder keys from content
      const usedKeys = [];
      const regex = /\{\{(\s*\w+\s*)\}\}/g;
      let match;
      while ((match = regex.exec(content)) !== null) {
        const key = match[1].trim();
        if (!usedKeys.includes(key)) usedKeys.push(key);
      }

      const data = {
        name: name.trim(),
        description: description.trim() || null,
        category: category || null,
        practice_area: practiceArea.trim() || null,
        content,
        placeholders: usedKeys,
      };

      if (template?.id) {
        await documentTemplateService.update(template.id, data);
      } else {
        await documentTemplateService.create(data);
      }
      onClose(true);
    } catch {
      showError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Group placeholders by group
  const groupedPlaceholders = {};
  placeholders.forEach(p => {
    if (!groupedPlaceholders[p.group]) groupedPlaceholders[p.group] = [];
    groupedPlaceholders[p.group].push(p);
  });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <Notification {...notification} onClose={hideNotification} />
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {template ? 'Edit Template' : 'New Document Template'}
          </h2>
          <button onClick={() => onClose(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left panel: Template fields + content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Standard Engagement Letter"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Practice Area</label>
                <input
                  type="text"
                  value={practiceArea}
                  onChange={(e) => setPracticeArea(e.target.value)}
                  placeholder="e.g., Divorce, Real Estate Closing"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this template..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Content editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Content
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Use {'{{placeholder_name}}'} syntax to insert auto-fill fields. Click placeholders in the sidebar to insert them.
              </p>
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`Enter your template content here...\n\nExample:\n\nDear {{client_full_name}},\n\nRe: {{case_title}} (Case No. {{case_number}})\n\nThis letter confirms that {{firm_name}} has been retained to represent you in the above-referenced matter...`}
                className="w-full h-96 px-4 py-3 border border-gray-300 rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
              />
            </div>
          </div>

          {/* Right panel: Placeholder palette */}
          <div className="w-64 border-l border-gray-200 overflow-y-auto bg-gray-50 p-4 flex-shrink-0">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <Tag className="h-4 w-4" />
              Placeholders
            </h3>
            <p className="text-xs text-gray-400 mb-3">Click to insert at cursor position</p>
            <div className="space-y-1">
              {Object.entries(groupedPlaceholders).map(([group, items]) => (
                <div key={group}>
                  <button
                    onClick={() => toggleGroup(group)}
                    className="flex items-center gap-1 w-full text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-1.5 hover:text-gray-900"
                  >
                    {expandedGroups[group] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    {group}
                  </button>
                  {expandedGroups[group] && (
                    <div className="space-y-0.5 mb-2">
                      {items.map(p => (
                        <button
                          key={p.key}
                          onClick={() => insertPlaceholder(p.key)}
                          className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-blue-50 hover:text-blue-700 transition-colors group"
                          title={p.example ? `Example: ${p.example}` : ''}
                        >
                          <span className="font-medium">{p.label}</span>
                          {p.example && (
                            <span className="block text-gray-400 group-hover:text-blue-400 truncate">
                              {p.example}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => onClose(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentTemplateEditor;
